"use client";

import { useEffect, useState } from "react";
import Link from "next/link";
import { Menu, X } from "lucide-react";
import { TwinMark } from "@product/components/app/twin-mark";
import {
  ACCENT_TEXT,
  DISPLAY,
  EDITORIAL_ACTION_CLS,
  EDITORIAL_CONTAINER_CLS,
  GROUND,
  INK,
  LABEL_CLS,
  mix,
  SURFACE_DARK,
} from "./theme";
import { ThemeSwatches, useThemePickerHotkey } from "./theme-switcher";
import type { Cta } from "./types";

export type NavLink = { label: string; href: string };

/**
 * Editorial top nav. Transparent with light text over the dark hero,
 * solidifying to a cream bar with ink text + hairline once scrolled past
 * the hero. Wordmark + anchor links + a single CTA. Fixed; sits above a
 * HeroSection rendered with showEyebrow={false}.
 */
export function EditorialNav({
  wordmark,
  eyebrow,
  links,
  cta,
  themePicker = false,
  solidFromStart = false,
  dark = false,
}: {
  wordmark: string;
  eyebrow?: string;
  links: NavLink[];
  cta: Cta;
  /** Host the palette swatches in the nav's right cluster (top-right),
   *  replacing the floating ThemeSwitcher on pages that have a nav. */
  themePicker?: boolean;
  /** Force the solid cream bar from the top — for pages without a
   *  full-height dark hero behind the nav (e.g. about, blog, legal). */
  solidFromStart?: boolean;
  /** Keep the complete navigation chrome on the editorial dark surface. */
  dark?: boolean;
}) {
  const [scrolled, setScrolled] = useState(false);
  const [menuOpen, setMenuOpen] = useState(false);
  const solid = !dark && (solidFromStart || scrolled || menuOpen);
  // The palette picker is hidden from end users until the "t" hotkey
  // reveals it; only listen on pages that actually host the picker.
  const showPicker = useThemePickerHotkey(themePicker);

  useEffect(() => {
    if (solidFromStart || dark) return;
    const onScroll = () => {
      // Flip to the solid bar as the full-height dark hero leaves view.
      setScrolled(window.scrollY > Math.max(80, window.innerHeight - 96));
    };
    onScroll();
    window.addEventListener("scroll", onScroll, { passive: true });
    return () => window.removeEventListener("scroll", onScroll);
  }, [dark, solidFromStart]);

  useEffect(() => {
    const closeAtDesktop = (event: MediaQueryListEvent) => {
      if (event.matches) setMenuOpen(false);
    };
    const media = window.matchMedia("(min-width: 768px)");
    media.addEventListener("change", closeAtDesktop);
    return () => media.removeEventListener("change", closeAtDesktop);
  }, []);

  const fg = solid ? INK : GROUND;
  const navBackground = dark ? SURFACE_DARK : solid ? GROUND : "transparent";
  const navRule = dark
    ? mix("--theme-ground", 14)
    : solid
      ? mix("--theme-ink", 12)
      : "transparent";

  return (
    <header
      className="fixed inset-x-0 top-0 z-50 transition-colors duration-300"
      style={{
        backgroundColor: navBackground,
        borderBottom: `1px solid ${navRule}`,
        backdropFilter: solid || dark ? "blur(8px)" : "none",
      }}
    >
      <div className={`flex min-h-17 items-center justify-between ${EDITORIAL_CONTAINER_CLS}`}>
        {/* The six-ring mark carries the brand; the wordmark string stays as
            the link's accessible name. Transparent ground works over both the
            dark hero and the solid cream bar. */}
        <Link href="/" aria-label={wordmark} className="flex items-center gap-2.5" style={DISPLAY}>
          <TwinMark className="size-7" />
          {eyebrow && (
            <span
              className={`hidden sm:inline ${LABEL_CLS}`}
              style={{
                color: solid ? mix("--theme-ink", 80) : mix("--theme-ground", 80),
              }}
            >
              {eyebrow}
            </span>
          )}
        </Link>

        <div className="flex items-center gap-4 md:gap-6">
          <nav className="hidden items-center gap-8 md:flex">
            {links.map((l) => (
              <a
                key={l.href}
                href={l.href}
                className="text-[0.92rem] font-medium transition-opacity hover:opacity-70"
                style={{ color: fg }}
              >
                {l.label}
              </a>
            ))}
          </nav>

          {showPicker && <ThemeSwatches size="sm" />}

          <a
            href={cta.href}
            className={`${EDITORIAL_ACTION_CLS} max-sm:hidden`}
            style={{
              backgroundColor: solid ? SURFACE_DARK : GROUND,
              color: solid ? GROUND : ACCENT_TEXT,
            }}
          >
            {cta.label}
          </a>

          <button
            type="button"
            aria-expanded={menuOpen}
            aria-controls="editorial-mobile-nav"
            aria-label={menuOpen ? "Close navigation" : "Open navigation"}
            className="inline-flex size-11 items-center justify-center md:hidden"
            style={{ color: fg }}
            onClick={() => setMenuOpen((open) => !open)}
          >
            {menuOpen ? <X aria-hidden="true" /> : <Menu aria-hidden="true" />}
          </button>
        </div>
      </div>
      <nav
        id="editorial-mobile-nav"
        aria-hidden={!menuOpen}
        className={`border-t md:hidden ${menuOpen ? "block" : "hidden"}`}
        style={{
          borderColor: dark ? mix("--theme-ground", 14) : mix("--theme-ink", 14),
          backgroundColor: dark ? SURFACE_DARK : GROUND,
        }}
      >
        <div className={`${EDITORIAL_CONTAINER_CLS} flex flex-col py-3`}>
          {links.map((link) => (
            <a
              key={link.href}
              href={link.href}
              className="flex min-h-11 items-center border-b text-[0.95rem] font-medium last:border-b-0"
              style={{
                borderColor: dark ? mix("--theme-ground", 12) : mix("--theme-ink", 12),
                color: dark ? GROUND : INK,
              }}
              onClick={() => setMenuOpen(false)}
            >
              {link.label}
            </a>
          ))}
          <a
            href={cta.href}
            className={`${EDITORIAL_ACTION_CLS} mt-3 sm:hidden`}
            style={{
              backgroundColor: dark ? GROUND : SURFACE_DARK,
              color: dark ? ACCENT_TEXT : GROUND,
            }}
          >
            {cta.label}
          </a>
        </div>
      </nav>
    </header>
  );
}
